import React, { useEffect, useState } from 'react';

/**
 * Live data freshness indicator.
 * - Green (pulsing): < 30s old — LIVE
 * - Amber: 30s - 2min old — OK
 * - Red: > 2min old — STALE
 *
 * Props:
 *   timestamp: Date | number | string  // when the data was fetched
 *   size: 'xs' | 'sm' | 'md'           // default 'sm'
 *   showLabel: boolean                 // show "LIVE"/"OK"/"STALE" text
 *   label: string                      // custom label prefix (e.g. "Quote")
 */
export default function FreshnessDot({ timestamp, size = 'sm', showLabel = false, label = null, className = '' }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!timestamp) {
    return (
      <span className={`inline-flex items-center gap-1 ${className}`} data-testid="freshness-dot">
        <span className={`${sizeMap[size]} rounded-full bg-gray-600`} />
        {showLabel && <span className="text-[10px] uppercase tracking-wide text-gray-500 font-mono">—</span>}
      </span>
    );
  }

  const ts = timestamp instanceof Date ? timestamp.getTime() : new Date(timestamp).getTime();
  const ageSec = Math.max(0, Math.floor((now - ts) / 1000));

  let state;
  if (ageSec < 30) state = 'live';
  else if (ageSec < 120) state = 'ok';
  else state = 'stale';

  const ageText = ageSec < 60 ? `${ageSec}s` : ageSec < 3600 ? `${Math.floor(ageSec / 60)}m` : `${Math.floor(ageSec / 3600)}h`;
  const stateText = state === 'live' ? 'LIVE' : state === 'ok' ? 'OK' : 'STALE';
  const colorCls = state === 'live' ? 'bg-green-400' : state === 'ok' ? 'bg-amber-400' : 'bg-red-500';
  const textCls = state === 'live' ? 'text-green-400' : state === 'ok' ? 'text-amber-400' : 'text-red-400';
  const pulseCls = state === 'live' ? 'animate-pulse' : '';
  const glowCls = state === 'live' ? 'shadow-[0_0_6px_rgba(74,222,128,0.8)]' : state === 'ok' ? 'shadow-[0_0_4px_rgba(251,191,36,0.6)]' : '';

  return (
    <span
      className={`inline-flex items-center gap-1 ${className}`}
      title={`Data ${ageText} old — ${stateText}`}
      data-testid="freshness-dot"
      data-fresh-state={state}
    >
      <span className={`${sizeMap[size]} rounded-full ${colorCls} ${pulseCls} ${glowCls}`} />
      {showLabel && (
        <span className={`text-[10px] uppercase tracking-wide font-mono ${textCls}`}>
          {label ? `${label} ` : ''}{stateText} {ageText}
        </span>
      )}
    </span>
  );
}

const sizeMap = {
  xs: 'w-1.5 h-1.5',
  sm: 'w-2 h-2',
  md: 'w-2.5 h-2.5',
};
